"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pivotTableMenuForCapabilities = exports.PivotTable = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2018 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var CorePivotTable_1 = require("./CorePivotTable");
var sdk_model_1 = require("@gooddata/sdk-model");
var omit_1 = tslib_1.__importDefault(require("lodash/omit"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var constants_1 = require("./impl/base/constants");
/**
 * Prepares new execution matching pivot table props.
 *
 * @param props - pivot table props
 * @returns new prepared execution
 */
function prepareExecution(props) {
    var _a;
    var backend = props.backend, workspace = props.workspace, filters = props.filters, _b = props.sortBy, sortBy = _b === void 0 ? [] : _b, _c = props.execConfig, execConfig = _c === void 0 ? {} : _c;
    return (_a = backend
        .workspace(workspace)
        .execution()
        .forBuckets(getBuckets(props), filters)
        .withDimensions(pivotDimensions))
        .withSorting.apply(_a, sortBy).withExecConfig(execConfig);
}
function getBuckets(props) {
    var _a = props.measures, measures = _a === void 0 ? [] : _a, _b = props.rows, rows = _b === void 0 ? [] : _b, _c = props.columns, columns = _c === void 0 ? [] : _c, _d = props.totals, totals = _d === void 0 ? [] : _d;
    return [
        sdk_model_1.newBucket.apply(void 0, tslib_1.__spreadArrays([sdk_ui_1.BucketNames.MEASURES], measures)),
        // ATTRIBUTE for backwards compatibility with Table component. Actually ROWS
        sdk_model_1.newBucket.apply(void 0, tslib_1.__spreadArrays([sdk_ui_1.BucketNames.ATTRIBUTE], rows, totals)),
        sdk_model_1.newBucket.apply(void 0, tslib_1.__spreadArrays([sdk_ui_1.BucketNames.COLUMNS], columns)),
    ];
}
function pivotDimensions(def) {
    var buckets = def.buckets;
    var row = sdk_model_1.bucketsFind(buckets, sdk_ui_1.BucketNames.ATTRIBUTE);
    var columns = sdk_model_1.bucketsFind(buckets, sdk_ui_1.BucketNames.COLUMNS);
    var measures = sdk_model_1.bucketsFind(buckets, sdk_ui_1.BucketNames.MEASURES);
    var rowAttributeIds = row ? sdk_model_1.bucketAttributes(row).map(sdk_model_1.attributeLocalId) : [];
    var columnAttributeIds = columns ? sdk_model_1.bucketAttributes(columns).map(sdk_model_1.attributeLocalId) : [];
    var measuresItemIdentifiers = measures && !sdk_model_1.bucketIsEmpty(measures) ? [sdk_model_1.MeasureGroupIdentifier] : [];
    var totals = row ? sdk_model_1.bucketTotals(row) : [];
    var totalsProp = totals.length ? { totals: totals } : {};
    return [
        tslib_1.__assign({ itemIdentifiers: rowAttributeIds }, totalsProp),
        {
            itemIdentifiers: tslib_1.__spreadArrays(columnAttributeIds, measuresItemIdentifiers),
        },
    ];
}
var RenderPivotTable = /** @class */ (function (_super) {
    tslib_1.__extends(RenderPivotTable, _super);
    function RenderPivotTable() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    RenderPivotTable.prototype.render = function () {
        var _a = this.props, exportTitle = _a.exportTitle, backend = _a.backend, workspace = _a.workspace, _b = _a.config, config = _b === void 0 ? {} : _b;
        ts_invariant_1.default(backend, "Backend was not provided for PivotTable. Either pass it as a prop or use BackendContext.");
        ts_invariant_1.default(workspace, "Workspace was not provided for PivotTable. Either pass it as a prop or use WorkspaceContext.");
        var newProps = omit_1.default(this.props, ["measures", "rows", "columns", "totals", "filters", "sortBy"]);
        var pivotTableConfig = tslib_1.__assign(tslib_1.__assign({}, config), { menu: pivotTableMenuForCapabilities(backend.capabilities, config === null || config === void 0 ? void 0 : config.menu) });
        var corePivotProps = omit_1.default(newProps, ["backend", "workspace"]);
        var execution = prepareExecution(this.props);
        return (react_1.default.createElement(sdk_ui_1.IntlWrapper, { locale: this.props.locale },
            react_1.default.createElement(sdk_ui_1.IntlTranslationsProvider, null, function (translationProps) {
                return (react_1.default.createElement(CorePivotTable_1.CorePivotTableAgImpl, tslib_1.__assign({}, corePivotProps, { config: pivotTableConfig, intl: translationProps.intl, execution: execution, exportTitle: exportTitle || "PivotTable" })));
            })));
    };
    return RenderPivotTable;
}(react_1.default.Component));
var WrappedPivotTable = sdk_ui_1.withContexts(RenderPivotTable);
/**
 * [PivotTable](https://sdk.gooddata.com/gooddata-ui/docs/pivot_table_component.html)
 * is a component with bucket props measures, rows, columns, totals, sortBy, filters
 *
 * @public
 */
exports.PivotTable = function (props) {
    var _a = sdk_ui_1.useResolveValuesWithPlaceholders([props.measures, props.columns, props.rows, props.totals, props.filters, props.sortBy], props.placeholdersResolutionContext), measures = _a[0], columns = _a[1], rows = _a[2], totals = _a[3], filters = _a[4], sortBy = _a[5];
    return react_1.default.createElement(WrappedPivotTable, tslib_1.__assign({}, props, { measures: measures, columns: columns, rows: rows, totals: totals, filters: filters, sortBy: sortBy }));
};
/**
 * Given analytical backend capabilities and the desired aggregations menu config, this function will correct the menu
 * configuration so that it fits the capabilities.
 *
 * The function will explicitly set the options regardless of what is the (current) default value of the option if
 * it is not present in the menu. The backend capabilities are a hard stop for features.
 *
 * Note: the {@link PivotTable} will use this function out of the box to ensure the effective menu configuration
 * matches the backend capabilities. You don't need to use when creating a PivotTable.
 *
 * @param capabilities - backend capabilities
 * @param desiredMenu - aggregation menu configuration desired by the client
 * @public
 */
function pivotTableMenuForCapabilities(capabilities, desiredMenu) {
    var _a;
    if (desiredMenu === void 0) { desiredMenu = {}; }
    var effectiveMenu = tslib_1.__assign({}, desiredMenu);
    if (!capabilities.canCalculateGrandTotals) {
        return {
            aggregations: false,
        };
    }
    if (!capabilities.canCalculateSubTotals) {
        effectiveMenu.aggregationsSubMenu = false;
    }
    if (!capabilities.canCalculateNativeTotals) {
        effectiveMenu.aggregationTypes = ((_a = effectiveMenu.aggregationTypes) !== null && _a !== void 0 ? _a : constants_1.AVAILABLE_TOTALS).filter(function (totalType) { return totalType !== "nat"; });
    }
    if (effectiveMenu.aggregationTypes && effectiveMenu.aggregationTypes.length === 0) {
        return {
            aggregations: false,
        };
    }
    return effectiveMenu;
}
exports.pivotTableMenuForCapabilities = pivotTableMenuForCapabilities;
//# sourceMappingURL=PivotTable.js.map